import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, Table, Alert, message, Button } from "antd";
import { ColumnsType } from "antd/lib/table";
import { apis, AUTH_HEADER_NAME } from "common/apis";
import {
  IAttendanceRosterUpload,
  IAttendanceRosterUploadRow,
} from "common/interface/IAttendanceRosterUpload";
import { routes } from "common/routes";
import { generateHeaders } from "common/utils";
import { AppPageHeader } from "components/AppPageHeader";
import { AppUpload } from "components/AppUpload";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import {
  removeProcessingAttendanceRoster,
  setProcessingAttendanceRoster,
} from "store/attendance-roster/attendanceRosterSlice";

const DEFAULT_HEADER_MAPPING =
  "userId:User ID,storeId:Store ID,phone:Phone,weekOffDays:Week Off Days,leaveDates:Leave Dates";

const SAMPLE_CSV_URL =
  "https://d69ugcdrlg41w.cloudfront.net/upload_samples/warehouse/attendance_roster_template.csv";

const UploadAttendanceRosterPage = () => {
  useDocumentTitle("Upload Attendance Roster");
  const { authToken } = useAppSelector((state) => state.app.user);
  const { processingAttendanceRoster } = useAppSelector((state) => state.attendanceRoster);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [hasErrorRows, setHasErrorRows] = useState(false);

  useEffect(() => {
    const { timeUploaded, key } = processingAttendanceRoster;
    if (timeUploaded) {
      const uploadedTime = new Date(timeUploaded).getTime();
      const currentTime = new Date().getTime();
      const minsPassed = (currentTime - uploadedTime) / 1000 / 60;
      if (minsPassed >= 15) {
        dispatch(removeProcessingAttendanceRoster());
        return;
      }
    }
    // key is non-null only when failed === 0 and data is staged (~15 min)
    if (key && !processingAttendanceRoster.failed) {
      navigate(`/${routes.ATTENDANCE_ROSTER.SAVE}`);
    }
    return () => {
      message.destroy();
    };
  }, [processingAttendanceRoster, navigate, dispatch]);

  useEffect(() => {
    setHasErrorRows(!!processingAttendanceRoster?.failedRows?.length);
  }, [processingAttendanceRoster?.failedRows]);

  const headers: { [name: string]: string } = {
    ...(authToken && { [AUTH_HEADER_NAME]: authToken }),
  };

  const onUploaded = (data: IAttendanceRosterUpload) => {
    dispatch(setProcessingAttendanceRoster(data));
  };

  const onUploading = () => {
    dispatch(removeProcessingAttendanceRoster());
    setHasErrorRows(false);
  };

  const headerMappingWithError = `${DEFAULT_HEADER_MAPPING},errors:Errors`;

  const generateMemoizedHeaders = useMemo(
    () => generateHeaders(headerMappingWithError),
    [headerMappingWithError]
  );

  const tableHeaders = generateMemoizedHeaders || {};
  const successRows = processingAttendanceRoster?.successRows || [];
  const failedRows = processingAttendanceRoster?.failedRows || [];
  const successData = successRows.map((row, i) => ({ ...row, key: `data-success-${i}` }));
  const failedData = failedRows.map((row, i) => ({ ...row, key: `data-failed-${i}` }));
  const data = [...failedData, ...successData];

  const columns: ColumnsType<IAttendanceRosterUploadRow & { key: string }> = [
    ...Object.keys(tableHeaders).map((key) => ({
      key,
      title: tableHeaders[key],
      dataIndex: key,
      render: (text: string, record: IAttendanceRosterUploadRow & { key: string }) => {
        if (key === "errors") {
          const errorMessages = record.errors?.map((e) => e.message).join(", ");
          return <>{errorMessages}</>;
        }
        return <>{text ?? ""}</>;
      },
    })),
  ];

  const addBorderToErrorRows = (record: IAttendanceRosterUploadRow & { key: string }) => {
    if (record.errors?.length) return "error-row";
    return "";
  };

  const showValidationErrors =
    !!Object.keys(processingAttendanceRoster).length && !processingAttendanceRoster.key;

  return (
    <>
      <Row justify="space-between" align="middle">
        <Col>
          <AppPageHeader title="Upload Attendance Roster" />
        </Col>
        <Col>
          <Button type="primary" onClick={() => window.open(SAMPLE_CSV_URL, "_blank")}>
            Download Sample File
          </Button>
        </Col>
      </Row>

      <Row>
        <Col lg={16} xs={24}>
          <AppUpload
            uploadUrl={apis.uploadAttendanceRoster}
            headers={headers}
            onUploaded={onUploaded}
            onUploading={onUploading}
            hasErrorRows={hasErrorRows}
          />
        </Col>
      </Row>

      {showValidationErrors && (
        <>
          <Row>
            <Col lg={16} xs={24}>
              <Alert
                type="error"
                message="Errors in sheet data. Nothing was staged — please fix and upload again."
                description={`Total: ${processingAttendanceRoster.total ?? 0}, Success: ${
                  processingAttendanceRoster.success ?? 0
                }, Failed: ${processingAttendanceRoster.failed ?? 0}, Skipped: ${
                  processingAttendanceRoster.skipped ?? 0
                }`}
                showIcon
                banner
              />
            </Col>
          </Row>
          <Row>
            <Table
              size="small"
              columns={columns}
              dataSource={data}
              rowClassName={(record) => addBorderToErrorRows(record)}
              scroll={{ y: 500, x: "100vh" }}
              className="table-with-errors"
            />
          </Row>
        </>
      )}
    </>
  );
};

export default UploadAttendanceRosterPage;
